import { FC, useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { Card, Checkbox, Col } from 'antd'
import { CompanyTiny, KudosFilterInput, EmployeeSearch, Country, CompanyScope } from '../../../types'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { useDescriptionSearch } from '../hooks/useDescriptionSearch'
import { EmployeeSearchSelect } from './EmployeeSearchSelect'
import { CompanySearchSelect } from './CompanySearchSelect'
import { CountrySelect } from './CountrySelect'
import { MonthYearFilter } from './MonthYearFilter'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { useCountriesList } from '../../../graphql/countrySettings/hooks/useCountriesList'
import { useActiveEmployeeSearch } from '../../employees/EmployeeSearch/useActiveEmployeeSearch'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../../graphql/kudos/util'
import CreateAttentionPointModalWithEmployeeSelect from '../../../components/custom_components/CreateAttentionpointModal/CreateAttentionPointModalWithEmployeeSelect'

interface AttentionPointInfoBoxProps {
  filters: KudosFilterInput
  onFiltersChange: (filters: KudosFilterInput) => void
}

export const AttentionPointInfoBox: FC<AttentionPointInfoBoxProps> = ({ filters, onFiltersChange }) => {
  const { t } = useTranslation()

  const employeeSearch = useActiveEmployeeSearch(false)
  const authorSearch = useActiveEmployeeSearch(false)
  const descriptionSearch = useDescriptionSearch((value) => {
    onFiltersChange({
      ...filters,
      searchTerm: value,
    })
  })

  // Initialize description search with existing filter
  useEffect(() => {
    if (filters.searchTerm && descriptionSearch.description === '') {
      descriptionSearch.initializeDescription(filters.searchTerm)
    }
  }, [filters.searchTerm, descriptionSearch])

  const companySearch = useCompanySearch(CompanyScope.All)
  const countrySearch = useCountriesList()

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearch | null>(null)
  const [selectedAuthor, setSelectedAuthor] = useState<EmployeeSearch | null>(null)
  const [selectedEmployeeCompany, setSelectedEmployeeCompany] = useState<CompanyTiny | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [isCreateAttentionPointModalVisible, setIsCreateAttentionPointModalVisible] = useState(false)

  // Employee option selection handler
  const handleEmployeeOptionClick = useCallback(
    (employee: EmployeeSearch) => {
      setSelectedEmployee(employee)
      onFiltersChange({
        ...filters,
        employeeId: employee.id,
      })
      employeeSearch.handleClear()
    },
    [filters, onFiltersChange, employeeSearch],
  )

  // Author option selection handler
  const handleAuthorOptionClick = useCallback(
    (author: EmployeeSearch) => {
      setSelectedAuthor(author)
      onFiltersChange({
        ...filters,
        authorId: author.id,
        filterByMe: false, // Decheck filterByMe when selecting author
      })
      authorSearch.handleClear()
    },
    [filters, onFiltersChange, authorSearch],
  )

  // Handle clearing employee selection
  const handleEmployeeClear = useCallback(() => {
    setSelectedEmployee(null)
    onFiltersChange({
      ...filters,
      employeeId: undefined,
    })
    employeeSearch.handleClear()
  }, [filters, onFiltersChange, employeeSearch])

  // Handle clearing author selection
  const handleAuthorClear = useCallback(() => {
    setSelectedAuthor(null)
    onFiltersChange({
      ...filters,
      authorId: undefined,
    })
    authorSearch.handleClear()
  }, [filters, onFiltersChange, authorSearch])

  // Handle employee company selection
  const handleEmployeeCompanyOptionClick = useCallback(
    (company: CompanyTiny) => {
      setSelectedEmployeeCompany(company)
      onFiltersChange({
        ...filters,
        employeeCompanyId: company.id,
      })
      companySearch.handleClear()
    },
    [filters, onFiltersChange, companySearch],
  )

  // Handle clearing company selection
  const handleCompanyClear = useCallback(() => {
    setSelectedEmployeeCompany(null)
    onFiltersChange({
      ...filters,
      employeeCompanyId: undefined,
    })
    companySearch.handleClear()
  }, [filters, onFiltersChange, companySearch])

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country | null) => {
      setSelectedCountry(country)
      onFiltersChange({
        ...filters,
        country: country || undefined,
      })
    },
    [filters, onFiltersChange],
  )

  const handleFilterByMeChange = (e: any) => {
    // Clear selected author when checking "Show Only My Attention Points"
    if (e.target.checked) {
      setSelectedAuthor(null)
      authorSearch.handleClear()
    }

    onFiltersChange({
      ...filters,
      filterByMe: e.target.checked,
      authorId: e.target.checked ? undefined : filters.authorId, // Clear authorId when checking filterByMe
    })
  }

  const handleClearFilters = () => {
    setSelectedEmployee(null)
    setSelectedAuthor(null)
    setSelectedEmployeeCompany(null)
    setSelectedCountry(null)
    descriptionSearch.handleDescriptionClear()
    employeeSearch.handleClear()
    authorSearch.handleClear()
    companySearch.handleClear()
    onFiltersChange({
      filterByMe: false,
      employeeCompanyId: undefined,
      country: undefined,
      startDate: undefined,
      endDate: undefined,
      pagination: {
        offset: 0,
        limit: KUDOS_DASHBOARD_PAGE_SIZE,
      },
    })
  }

  const hasActiveFilters = () => {
    return !!(
      filters.searchTerm ||
      filters.authorId ||
      filters.employeeId ||
      filters.employeeCompanyId ||
      filters.country ||
      filters.startDate ||
      filters.endDate ||
      filters.filterByMe
    )
  }

  return (
    <Card
      bordered={false}
      style={{
        backgroundColor: 'white',
        boxShadow: 'none',
        borderRadius: '8px',
        height: '205px',
      }}
    >
      <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
        {/* Header with title and add buttons */}
        <PerFlexRow justify="space-between" align="middle" width="fullWidth">
          <PerFlexRow align="middle">
            <PerText appearance="tile-heading">{t('attentionPoint.management.title')}</PerText>
          </PerFlexRow>
          <PerButtonCircle
            icon="add_stroke"
            color="primary"
            variant="pastel"
            onClick={() => setIsCreateAttentionPointModalVisible(true)}
            tooltip={t('attentionPoint.management.createNewWithEmployee')}
            data-e2e="create-kudos-with-employee-button"
          />
        </PerFlexRow>

        <PerText mb="xxs" fontSize="s">
          {t('attentionPoint.management.dashboardDisclaimer')}
        </PerText>

        {/* Filters - Grid Layout */}

        <PerFlexRow>
          <PerFlexRow width="fullWidth" mb="xs">
            {/* Employee Name */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.employeeName')}
                </PerText>
                <EmployeeSearchSelect
                  searchTerm={employeeSearch.searchTerm}
                  onSearch={employeeSearch.handleSearch}
                  onClear={handleEmployeeClear}
                  employees={employeeSearch.employees}
                  loading={employeeSearch.loading}
                  shouldShowOptions={employeeSearch.shouldShowOptions}
                  onOptionClick={handleEmployeeOptionClick}
                  placeholder={t('attentionPoint.management.filters.employeeNamePlaceholder')}
                  selectedEmployee={selectedEmployee}
                  size={'small'}
                />
              </PerFlexRow>
            </Col>

            {/* Employee Company */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.employeeCompany')}
                </PerText>
                <CompanySearchSelect
                  searchTerm={companySearch.searchTerm}
                  onSearch={companySearch.handleSearch}
                  onClear={handleCompanyClear}
                  companies={companySearch.companies}
                  loading={companySearch.loading}
                  shouldShowOptions={companySearch.shouldShowOptions}
                  onOptionClick={handleEmployeeCompanyOptionClick}
                  placeholder={t('attentionPoint.management.filters.employeeCompanyPlaceholder')}
                  selectedCompany={selectedEmployeeCompany}
                />
              </PerFlexRow>
            </Col>

            {/* Country Filter */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.country')}
                </PerText>
                <CountrySelect
                  countries={countrySearch.countries}
                  selectedCountry={selectedCountry}
                  onCountrySelect={handleCountrySelect}
                  placeholder={t('attentionPoint.management.filters.countryPlaceholder')}
                  loading={countrySearch.loading}
                  onDropdownVisibleChange={(open) => {
                    if (open && countrySearch.countries.length === 0) {
                      countrySearch.loadCountries() // Load countries when dropdown opens
                    }
                  }}
                />
              </PerFlexRow>
            </Col>

            {/* From Date Filter */}
            <Col span={4}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.fromDate')}
                </PerText>
                <MonthYearFilter filters={filters} onFiltersChange={onFiltersChange} filterType="from" />
              </PerFlexRow>
            </Col>

            {/* To Date Filter */}
            <Col span={4}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.toDate')}
                </PerText>
                <MonthYearFilter filters={filters} onFiltersChange={onFiltersChange} filterType="to" />
              </PerFlexRow>
            </Col>
          </PerFlexRow>

          <PerFlexRow width="fullWidth">
            {/* Author Name */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('attentionPoint.management.filters.authorName')}
                </PerText>
                <EmployeeSearchSelect
                  searchTerm={authorSearch.searchTerm}
                  onSearch={authorSearch.handleSearch}
                  onClear={handleAuthorClear}
                  employees={authorSearch.employees}
                  loading={authorSearch.loading}
                  shouldShowOptions={authorSearch.shouldShowOptions}
                  onOptionClick={handleAuthorOptionClick}
                  placeholder={t('attentionPoint.management.filters.authorNamePlaceholder')}
                  selectedEmployee={selectedAuthor}
                  size={'small'}
                />
              </PerFlexRow>
            </Col>

            {/* Show attention Point assigned by me */}
            <Col span={9}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                  {t('attentionPoint.management.filters.showOnlyMine')}
                </PerText>
                <PerFlexRow align="middle">
                  <Checkbox checked={filters.filterByMe || false} onChange={handleFilterByMeChange} />
                  <PerText fontSize="s" color="secondary" ml="xxs">
                    {t('attentionPoint.management.filters.showOnlyMine')}
                  </PerText>
                </PerFlexRow>
              </PerFlexRow>
            </Col>

            <Col span={10}>
              <PerFlexRow justify="end" align="bottom" className="fullHeight">
                <PerButtonCircle
                  color="error"
                  icon="delete_fill"
                  variant="pastel"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters()}
                  tooltip={t('attentionPoint.management.filters.clear')}
                />
              </PerFlexRow>
            </Col>
          </PerFlexRow>
        </PerFlexRow>
      </PerFlexRow>

      {/* Create Attention Point Modal with Employee Select */}
      <CreateAttentionPointModalWithEmployeeSelect
        visible={isCreateAttentionPointModalVisible}
        closeModal={() => setIsCreateAttentionPointModalVisible(false)}
        onSuccess={() => {
          setIsCreateAttentionPointModalVisible(false)
        }}
        filters={{ ...filters, pagination: { offset: 0, limit: KUDOS_DASHBOARD_PAGE_SIZE } }}
      />
    </Card>
  )
}

export default AttentionPointInfoBox
